import {
  Button,
  DatePicker,
  Grid,
  GridColumn,
  GridRow,
  InplaceEdit,
  type RadioButtonType,
  RadioGroup,
  showToast,
} from '@bp/ui-components';
import classNames from 'classnames';
import { graphql } from 'client/gql';
import { BpText } from 'components/BpText/BpText';
import dayjs from 'dayjs';
import { Form, Formik, type FormikHelpers } from 'formik';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type CombinedError, useMutation, useQuery } from 'urql';
import { v4 } from 'uuid';
import { useAuthClaims } from '../../hooks/useAuthClaims';
import { useAvailableServicesForOrganization } from '../../hooks/useAvailableServicesForOrganization';
import { useGroupMembersCount } from '../../hooks/useGroupMembersCount';
import { backendApi, type TestForSufficientBBBSlotsInput } from '../../utils/backendApi';
import { buildRecurrenceRule, connectByUuid } from '../../utils/connectLib';
import { beginDuration, buildRecurrences, dateAndTime, eventColors } from '../../utils/dateCalculations';
import { showErrorToast } from '../../utils/showErrorToast';
import { showSuccessToast } from '../../utils/showSuccessToast';
import { AppointmentMeta } from '../Appointments/Forms/components/AppointmentMeta';
import type { AppointmentFormValues } from '../Appointments/types';
import { BpCard } from '../BpCard/BpCard';
import { DEFAULT_MEETING_DURATION } from '../Modals/InstantMeetingModal/InstantMeetingsForm';
import styles from './AppointmentForm.module.scss';
import { appointmentFormSchema } from './validation/appointmentFormSchema';

const CreateCalendarEventDocument = graphql(`
  mutation BpCreateCalendarEventInAppointmentForm($input: [CalendarEventCreateInput!]!) {
    createCalendarEvents(input: $input) {
      calendarEvents {
        uuid
        __typename
      }
    }
  }
`);

const UpdateCalendarEventDocument = graphql(`
  mutation BpUpdateCalendarEventsInAppointmentForm($where: CalendarEventWhere!, $update: CalendarEventUpdateInput!) {
    updateCalendarEvents(where: $where, update: $update) {
      calendarEvents {
        uuid
        __typename
        title
        start
        duration
      }
    }
  }
`);

const AppointmentFormPageQueryDocument = graphql(`
  query AppointmentFormPageData($appointmentUuid: ID!, $organizationUuid: ID!, $holderUuid: ID!, $groupUuid: ID!) {
    calendarEvents(options: { sort: [{ start: ASC }], limit: 500 }, where: { uuid: $appointmentUuid }) {
      uuid
      __typename
      start
      duration
      categories
      title
      description
      teachingUnit {
        uuid
        __typename
      }
      recurrenceRule {
        uuid
        __typename
        frequency
        until
      }
      recordVirtualEvent
      maxAttendees
    }
    ...UseAvailableServicesForOrganizationFields
    ...AppointmentMetaFields
    ...UseGroupMembersCountFields
    profilesAggregate(where: { organization: { uuid: $organizationUuid } }) {
      __typename
      count
    }
  }
`);

export type TErrors = AppointmentFormValues & { lessonType?: string };

type ConferenceContext = 'GroupOrCourse' | 'Profile' | 'Organization';

type AppointmentFormPropsType = {
  isModal?: boolean;
  canAssignTeachingUnit?: boolean;
  disableEventTypeValues?: string[];
  holderUuid: string;
  conferenceContext: ConferenceContext;
  organizationUuid: string;
  appointmentUuid?: string;
  teachingUnitUuid?: string;
  onClose: () => void;
};

const MAX_ATTENDEES_FALLBACK = 100;

const attendeesByContext = (
  context: ConferenceContext,
  participantsCount: number,
  profilesCount: number | undefined,
): number => {
  const DEFAULT_COUNT = 10;
  let _attendees = participantsCount ?? DEFAULT_COUNT;

  if (context === 'Organization') {
    _attendees = profilesCount ?? DEFAULT_COUNT;
  }

  if (context === 'Profile') {
    _attendees = DEFAULT_COUNT;
  }

  return _attendees;
};

export const AppointmentForm = ({
  isModal = false,
  canAssignTeachingUnit = true,
  disableEventTypeValues = [],
  holderUuid,
  conferenceContext,
  organizationUuid,
  appointmentUuid,
  onClose,
  teachingUnitUuid,
}: AppointmentFormPropsType) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const now = dayjs().add(1, 'hour').startOf('hour');

  const [loading, setLoading] = useState<boolean>(false);
  const [, bpCreateEvents] = useMutation(CreateCalendarEventDocument);
  const [, bpUpdateEvents] = useMutation(UpdateCalendarEventDocument);

  const [{ data }] = useQuery({
    query: AppointmentFormPageQueryDocument,
    variables: {
      appointmentUuid: appointmentUuid ?? '',
      organizationUuid,
      groupUuid: holderUuid,
      holderUuid,
    },
  });

  const calendarEvent = data?.calendarEvents.find((e) => e.uuid === appointmentUuid);
  const profilesCount = data?.profilesAggregate.count;

  const start = calendarEvent?.start ?? '';
  const end = dayjs(start).add(
    dayjs.duration(parseInt(calendarEvent?.duration ?? '', 10) ?? DEFAULT_MEETING_DURATION * 60, 'seconds'),
  );

  // TODO: what to do if holder is not a group but a profile ?
  const { groupMembers } = useGroupMembersCount(data);

  const availableServices = useAvailableServicesForOrganization(data);
  const categories = useMemo(() => calendarEvent?.categories || ['onSite'], [calendarEvent?.categories]);

  const bbbMaxAttendees = () => {
    const max = availableServices.find((s) => s.service === 'BigBlueButton')?.maxParticipantsPerMeeting;
    if (max === 0) return Infinity;
    if (!max) return MAX_ATTENDEES_FALLBACK;

    return max;
  };

  // if only one service available, make it default
  const defaultService = useMemo(() => {
    const services: { value: string; label: string }[] = [];
    if (availableServices.find((s) => s.service === 'Zoom')) {
      services.push({ value: 'zoom', label: 'Zoom' });
    }
    if (availableServices.find((s) => s.service === 'BigBlueButton')) {
      services.push({ value: 'bbb', label: 'BigBlueButton' });
    }

    let serviceFromCategory = services.find((opt) => categories.includes(opt.value));
    const onlineCategories = categories.filter((c) => c !== 'onSite');

    if (!serviceFromCategory && services.length === 1 && onlineCategories.length > 0) {
      serviceFromCategory = services[0];
      if (!categories.includes(serviceFromCategory.value)) {
        categories.push(serviceFromCategory.value);
      }
    }
    return serviceFromCategory ?? { value: '', label: '' };
  }, [availableServices, categories]);

  const initialValues: AppointmentFormValues = {
    title: calendarEvent?.title || '',
    categories: categories,
    desc: calendarEvent?.description || '',
    startDate: calendarEvent?.start ? dayjs(calendarEvent?.start) : now,
    startTime: calendarEvent?.start ? dayjs(calendarEvent?.start) : now,
    endTime: calendarEvent?.duration ? end : now.add(DEFAULT_MEETING_DURATION * 60, 'seconds'),
    endDate: calendarEvent?.duration ? end : now,
    teachingUnit: canAssignTeachingUnit ? calendarEvent?.teachingUnit?.uuid || teachingUnitUuid : undefined,
    recurrence: calendarEvent?.recurrenceRule?.frequency || 'once',
    until: dayjs(calendarEvent?.recurrenceRule?.until),
    record: calendarEvent?.recordVirtualEvent || false,
    duration: calendarEvent?.duration ? parseInt(calendarEvent.duration, 10) : DEFAULT_MEETING_DURATION * 60,
    service: defaultService,
    participantsCount: calendarEvent?.maxAttendees ?? groupMembers + 10,
    bbbMaxAttendees: bbbMaxAttendees(),
  };

  const showToasts = (count: number, dateString: string | undefined) => {
    if (count > 0)
      showToast(`${t('meetings.tooManyParticipants')}: ${dayjs(dateString).format('DD.MM.YYYY')} - max ${count}`, {
        type: 'error',
      });

    if (count < 1)
      showToast(`${t('meetings.noSlots')}: ${dayjs(dateString).format('DD.MM.YYYY')}`, {
        type: 'error',
      });
  };

  const hasBBBSlots = async (recurrences: TestForSufficientBBBSlotsInput[], attendees: number): Promise<boolean> => {
    const response = await Promise.all(recurrences.map(async (r) => await backendApi.testForSufficientBBBSlots(r)));
    const eventSlots: { [uuid: string]: number }[] = response.flat();

    for (const event of eventSlots) {
      const [eventId, slots] = Object.entries(event)[0];
      if (slots < 0) {
        const feCount = attendees + slots;
        showToasts(feCount, recurrences.find((r) => r.id === eventId)?.start);
        return false;
      }
    }
    return true;
  };

  const handleError = (error: CombinedError | undefined, resetForm: () => void) => {
    if (error) {
      showErrorToast(error);
    } else {
      resetForm();
      showSuccessToast(t('common.saved'));
      onClose();
    }
  };

  const handleSubmit = async (values: AppointmentFormValues, formikHelpers: FormikHelpers<AppointmentFormValues>) => {
    if (loading) return;

    setLoading(true);

    const {
      categories,
      title,
      startTime,
      endTime,
      startDate,
      endDate,
      desc,
      teachingUnit,
      recurrence,
      service,
      participantsCount,
      record,
      until,
    } = values;

    let canSave = true;

    const attendees = attendeesByContext(conferenceContext, participantsCount, profilesCount);

    // check if we have sufficient bbb connections
    if (values.service.value === 'bbb') {
      const recurrences: TestForSufficientBBBSlotsInput[] = [];
      if (recurrence === 'once') {
        recurrences.push({
          id: crypto.randomUUID(),
          maxAttendees: attendees,
          start: startTime.toISOString(),
          end: endTime.toISOString(),
          organization: organizationUuid,
        });
      }
      if (recurrence !== 'once') {
        recurrences.push(
          ...buildRecurrences(values).map((r) => ({
            id: crypto.randomUUID(),
            maxAttendees: attendees,
            start: dateAndTime(r, startTime).toISOString(),
            end: dateAndTime(r, endTime).toISOString(),
            organization: organizationUuid,
            until: until.toISOString(),
          })),
        );
      }

      canSave = await hasBBBSlots(recurrences, attendees);
      setLoading(false);
    }

    //  we have sufficient bbb connections or categories are onSite only
    if (canSave || categories.every((c) => c === 'onSite')) {
      // only add service if the categories are hybrid or virtual
      const cats: string[] =
        service?.value && (categories.includes('hybrid') || categories.includes('virtual'))
          ? [...categories, service.value as string]
          : categories;

      if (appointmentUuid) {
        // Edit Event
        const { error } = await bpUpdateEvents({
          update: {
            start: beginDuration(startTime, startDate, endTime, endDate).begin.toISOString(),
            duration: beginDuration(startTime, startDate, endTime, endDate).duration.toString(),
            title,
            description: desc,
            color: eventColors(categories),
            categories: cats,
            maxAttendees: participantsCount,
            guestsAllowed: true, // per default guests are allowed (as it was in the old system too)
            recordVirtualEvent: record,
            ...(teachingUnit
              ? {
                  teachingUnit: {
                    disconnect: {},
                    ...connectByUuid(teachingUnit),
                  },
                }
              : { teachingUnit: { disconnect: {} } }),
          },
          where: { uuid: appointmentUuid },
        });
        handleError(error, formikHelpers.resetForm);
      } else {
        // New Event
        const input = {
          foreignRefId: [],
          organization: connectByUuid(organizationUuid),
          ...(teachingUnit && { teachingUnit: connectByUuid(teachingUnit) }),
          holder:
            conferenceContext === 'GroupOrCourse'
              ? { Group: connectByUuid(holderUuid) }
              : conferenceContext === 'Profile'
                ? { Profile: connectByUuid(holderUuid) }
                : { Organization: connectByUuid(holderUuid) },
          start: beginDuration(startTime, startDate, endTime, endDate).begin.toISOString(),
          duration: beginDuration(startTime, startDate, endTime, endDate).duration,
          title,
          description: desc,
          color: eventColors(categories),
          owner: connectByUuid(pimAuthClaims.getProfile().uuid ?? ''),
          maxAttendees: participantsCount,
          guestsAllowed: true, // per default guests are allowed (as it was in the old system too)
          recordVirtualEvent: record,
          categories: cats,
          ...buildRecurrenceRule(recurrence, until),
        };

        const { error } = await bpCreateEvents({ input });
        handleError(error, formikHelpers.resetForm);
      }
      setLoading(false);
    }
  };

  const recurrenceOptions: RadioButtonType[] = [
    {
      label: t('appointments.recurrences.once'),
      value: 'once',
    },
    {
      label: t('appointments.recurrences.daily'),
      value: 'daily',
    },
    {
      label: t('appointments.recurrences.weekly'),
      value: 'weekly',
    },
    {
      label: t('appointments.recurrences.workdays'),
      value: 'workdays',
    },
    {
      label: t('appointments.recurrences.yearly'),
      value: 'yearly',
    },
  ];

  return (
    <div className={styles['appointment-form']}>
      <Formik<AppointmentFormValues>
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={appointmentFormSchema}
      >
        {({ values, errors, resetForm, isSubmitting, setFieldTouched, setFieldValue }) => {
          return (
            <Form
              className={classNames('bp__form', {
                'is-modal': isModal,
                [styles.modal]: isModal,
              })}
            >
              <div className={isModal ? 'bp__modal-header' : 'bp__form-header'}>
                <InplaceEdit
                  className={styles.title}
                  name='title'
                  size='xxl'
                  onBlur={(value) => {
                    void setFieldValue('title', value);
                    void setFieldTouched('title', true);
                  }}
                  onChange={(value) => {
                    void setFieldValue('title', value);
                    void setFieldTouched('title', true);
                  }}
                  defaultValue={values.title}
                  placeholder={t('common.addTitle')}
                  error={errors.title}
                  dense
                  backgroundColor={isModal ? 'white' : 'background'}
                />
                <div className={'bp__form-buttons'}>
                  <Button
                    hierarchy='secondary'
                    onClick={() => {
                      resetForm();
                      onClose();
                    }}
                  >
                    {appointmentUuid ? t('common.discardChanges') : t('common.cancel')}
                  </Button>
                  <Button
                    hierarchy='primary'
                    type={'submit'}
                    disabled={!!Object.keys(errors).length}
                    isLoading={isSubmitting}
                  >
                    {t('common.save')}
                  </Button>
                </div>
              </div>

              <Grid>
                <GridRow mobileGap='var(--grid-column-gap)'>
                  <GridColumn width={6}>
                    <AppointmentMeta
                      query={data}
                      availableServices={availableServices}
                      disableEventTypeValues={disableEventTypeValues ?? []}
                      canAssignTeachingUnit={canAssignTeachingUnit}
                    />
                  </GridColumn>
                  <GridColumn width={6}>
                    <BpCard header={{ headline: t('appointments.recurrence') }}>
                      {appointmentUuid ? (
                        <BpText className='mb-3' embedded>
                          {t('appointments.recurrenceEditDescription')}
                        </BpText>
                      ) : (
                        <BpText className='mb-3' embedded>
                          {t('appointments.recurrenceDescription')}
                        </BpText>
                      )}
                      <RadioGroup
                        name='recurrence'
                        disabled={!!appointmentUuid}
                        value={values.recurrence ?? 'once'}
                        options={recurrenceOptions}
                        onChange={async (value) => {
                          await setFieldTouched('recurrence');
                          await setFieldValue('recurrence', value);
                        }}
                      />
                      {values.recurrence !== 'once' && (
                        <DatePicker
                          disabled={!!appointmentUuid}
                          label={t('appointments.until')}
                          onChange={async (e) => {
                            await setFieldValue('until', dayjs(e));
                            await setFieldTouched('until', true);
                          }}
                          name={'until'}
                          value={values.until.toDate()}
                        />
                      )}
                    </BpCard>
                  </GridColumn>
                </GridRow>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};
